import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createArticle, updatedArticle, getGuru, uploadFile, getArticleById } from '../composables/fetchAPI';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import SearchBar from '../components/SearchBar';
import { isCryptoSymbol, cryptoPairLabel } from '../composables/fetchCrypto';

const DEFAULT_TAGS = ['news', 'analysis', 'earnings', 'alpha'];

// ticker คริปโตเก็บเป็น X:BTCUSD แต่แสดงเป็น BTCUSD (ตัด X: ออก)
const tickerLabel = (ticker: string) => cryptoPairLabel(ticker);

const ArticlePreviewModal: React.FC<{
  open: boolean;
  onClose: () => void;
  article: {
    title: string;
    content: string;
    tags: string[];
    tickers: string[];
    guruName?: string;
    portfolioName?: string;
    cover?: string;
  };
}> = ({ open, onClose, article }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"
      onClick={onClose}
      style={{ cursor: 'pointer' }}
    >
      <div
        className="bg-[#18181b] rounded-xl shadow-lg max-w-2xl w-full p-8 relative overflow-y-auto"
        style={{
          maxHeight: '80vh',
          cursor: 'default',
          scrollbarWidth: 'none', // Firefox
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* ปุ่มกากบาทแดง ลอยขวาบน (fixed) */}
        <button
          className="w-10 h-10 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-700 text-white text-2xl font-bold shadow-lg z-[100]"
          onClick={onClose}
          title="ปิด"
          style={{
            position: 'fixed',
            top: 'calc(50% - 42vh)', // ปรับตำแหน่งตาม modal
            right: 'calc(50% - 360px)', // ปรับตำแหน่งตาม modal (max-w-2xl ≈ 672px/2)
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <span style={{ color: 'white', fontSize: '1.7rem', fontWeight: 900 }}>×</span>
        </button>
        {/* ซ่อน scrollbar */}
        <style>{`
          .preview-modal::-webkit-scrollbar { display: none; }
        `}</style>
        <div className="preview-modal">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <h1 className="text-2xl font-bold mb-2">{article.title}</h1>
            {Array.isArray(article.tags) && article.tags.length > 0 && (
              <span className="text-sm font-normal" style={{ color: 'var(--secondary-text-color)' }}>
                {article.tags.join(' / ')}
              </span>
            )}
          </div>
          {/* <div className="mb-2" style={{ color: 'var(--secondary-text-color)' }}>
            <span>Portfolio: {article.portfolioName || '-'}</span>
          </div> */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {Array.isArray(article.tickers) && article.tickers.map((ticker, idx) => (
              <span
                key={idx}
                className={`${isCryptoSymbol(ticker) ? 'bg-orange-400 border-orange-300' : 'bg-yellow-400 border-yellow-300'} text-gray-900 px-2 py-0.5 rounded-full text-xs font-semibold border`}
              >
                {tickerLabel(ticker)}
              </span>
            ))}
          </div>
          {article.cover && (
            <div className="mb-6 flex justify-center">
              <img
                src={article.cover}
                alt="Article"
                className="rounded shadow"
                style={{
                  width: '500px',
                  maxWidth: '90%',
                  height: 'auto',
                }}
              />
            </div>
          )}
          <div
            className="mt-2 min-h-24 text-base break-words max-w-full border-t pt-6 prose prose-invert"
            style={{
              color: 'var(--text-color)',
              borderBlockColor: 'var(--secondary-text-color)',
            }}
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
          <div className="mt-8 pt-4 border-t text-sm flex flex-wrap gap-4" style={{ color: 'var(--secondary-text-color)', borderBlockColor: 'var(--secondary-text-color)' }}>
            <span>{article.guruName || '-'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const CreateArticlePage: React.FC = () => {
  const { articleId } = useParams<{ articleId?: string }>();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [status, setStatus] = useState('published');
  const [tickers, setTickers] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [guruId, setGuruId] = useState('');
  const [portfolioId, setPortfolioId] = useState('523c566c-7863-41d3-9e13-29742284c26b');
  const [guruList, setGuruList] = useState<any[]>([]);
  const [portfolioOptions, setPortfolioOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const navigate = useNavigate();
useEffect(() => {
  if (guruId) {
    const selectedGuru = guruList.find((g: any) => g.id === guruId);
    setPortfolioOptions(selectedGuru?.portfolios || []);

    // Hardcode portfolioId based on guruId
    if (guruId === '2d51d1c5-e764-4503-9293-3a98c5d39123') {
      setPortfolioId('523c566c-7863-41d3-9e13-29742284c26b');
    } else if (guruId === '5f8adbab-f7ed-4564-9c18-43e81f74c244') {
      setPortfolioId('d1a45491-9b10-4b18-a717-af5420398970');
    } else if (guruId === 'eba14023-edb7-4fed-aa59-0dd2ae07de46') {
      setPortfolioId('4a89194a-55c6-4298-a78a-cece413ec670');
    } else {
      setPortfolioId(''); // Reset if no matching guruId
    }
  } else {
    setPortfolioOptions([]);
    setPortfolioId('');
  }
}, [guruId, guruList]);
  useEffect(() => {
    getGuru().then(data => setGuruList(data));
  }, []);

  useEffect(() => {
    if (guruId) {
      const selectedGuru = guruList.find((g: any) => g.id === guruId);
      setPortfolioOptions(selectedGuru?.portfolios || []);
      // setPortfolioId('');
    } else {
      setPortfolioOptions([]);
      // setPortfolioId('');
    }
  }, [guruId, guruList]);

  // ถ้าเป็น edit ให้ดึงข้อมูลบทความมาเติม
  useEffect(() => {
    const fetchArticle = async () => {
      if (articleId) {
        setLoading(true);
        try {
          const data = await getArticleById(articleId);
          setTitle(data.title || '');
          setContent(data.content || '');
          setStatus(data.status || 'published');
          setTickers(data.tickers || []);
          setTags(data.tags || []);
          setGuruId(data.guruId || '');
          // setPortfolioId(data.portfolioId || '');
          setImageUrl(data.cover || '');
        } catch {
          setError('โหลดข้อมูลบทความไม่สำเร็จ');
        }
        setLoading(false);
      }
    };
    fetchArticle();
  }, [articleId]);

  // Add/remove ticker/tag handlers
  const handleAddTicker = (ticker: string) => {
    if (!tickers.includes(ticker)) setTickers([...tickers, ticker]);
  };
  const handleRemoveTicker = (ticker: string) => {
    setTickers(tickers.filter(t => t !== ticker));
  };
  const handleAddTag = (tag: string) => {
    if (!tags.includes(tag)) setTags([...tags, tag]);
  };
  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter(t => t !== tag));
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      try {
        const res = await uploadFile(file);
        setImageUrl(res.url);
      } catch {
        setError('Failed to upload image');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (articleId) {
        await updatedArticle(articleId, {
          guruId,
          portfolioId,
          tickers,
          tags,
          title,
          content,
          status,
          cover: imageUrl,
        });
      } else {
        await createArticle({
          guruId,
          portfolioId,
          tickers,
          tags,
          title,
          content,
          status,
          cover: imageUrl,
        });
      }
      navigate('/articles');
    } catch (err: any) {
      setError(articleId ? 'Failed to update article' : 'Failed to create article');
    }
    setLoading(false);
  };

  const DropdownMultiSelect: React.FC<{
    options: string[];
    selected: string[];
    onSelect: (value: string) => void;
    placeholder: string;
  }> = ({ options, selected, onSelect, placeholder }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    // Close dropdown on outside click
    useEffect(() => {
      const handleClick = (e: MouseEvent) => {
        if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
      };
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    return (
      <div className="relative" ref={ref}>
        <button
          type="button"
          className="w-fit min-w-[120px] border border-[#333] px-2 py-1 rounded-md text-left bg-[#23232b] text-white placeholder-gray-400 text-sm"
          onClick={() => setOpen(!open)}
        >
          <span className="text-white">{placeholder}</span>
          <span className="float-right ml-2">&#9660;</span>
        </button>
        {open && (
          <div className="absolute z-10 mt-1 min-w-[120px] bg-[#23232b] border border-[#333] rounded-md shadow-lg max-h-40 overflow-auto text-sm">
            {options.filter(opt => !selected.includes(opt)).map(opt => (
              <div
                key={opt}
                className="px-4 py-1 hover:bg-yellow-100 hover:text-yellow-900 cursor-pointer text-white rounded"
                onClick={() => { onSelect(opt); setOpen(false); }}
              >
                {opt}
              </div>
            ))}
            {options.filter(opt => !selected.includes(opt)).length === 0 && (
              <div className="px-4 py-1 text-gray-400">No options</div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-2xl mx-auto p-8">
      <h1 className="text-3xl font-bold mb-8 text-center">
        {articleId ? 'Edit Article' : 'Create Article'}
      </h1>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Top row: Guru, Portfolio, Title */}
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block mb-1 font-semibold">Guru</label>
            <select
              className="px-3 py-2 rounded w-full bg-[#23232b] text-white border border-[#333]"
              value={guruId}
              onChange={e => setGuruId(e.target.value)}
              required
            >
              <option value="" >Select Guru</option>
              {guruList.map((g: any) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>
          {/* <div className="flex-1">
            <label className="block mb-1 font-semibold">Portfolio</label>
            <select
              className="px-3 py-2 rounded w-full bg-[#23232b] text-white border border-[#333]"
              value={portfolioId}
              onChange={e => setPortfolioId(e.target.value)}
              required
              disabled={!guruId}
            >
              <option value="" className='text-gray-700'>Select Portfolio</option>
              {portfolioOptions.map((p: any) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div> */}
          <div className="flex-[2]">
            <label className="block mb-1 font-semibold ">Title</label>
            <input
              type="text"
              className="px-3 py-2 rounded w-full bg-[#23232b] text-white border border-[#333] "
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder='Enter article title'
              required
            />
          </div>
        </div>
        {/* Content */}
        <div>
          <label className="block mb-1 font-semibold">Content</label>
          <style>{`
  .ql-container {
    border: none !important;
  }
  .ql-toolbar {
    border: none !important;
    border-bottom: 1px solid #949495 !important; /* Tailwind gray-200 */
  }
  `}</style>
          <ReactQuill
            theme="snow"
            value={content}
            onChange={setContent}
            className="bg-gray-100 rounded-xl text-gray-900 pb-7"
            placeholder='พิมพ์ที่นี่'
          />
        </div>
        {/* Ticker tags */}
        <div>
          <label className="block mb-1 font-semibold">Ticker</label>
          <div className="w-64">
            <SearchBar
              onSelect={handleAddTicker}
              selectedSymbols={tickers}
              persistSelection={false}
              includeCrypto
            />
          </div>
          {/* Selected tickers as tags */}
          <div className="flex gap-2 flex-wrap mt-2">
            {tickers.map(ticker => (
              <span
                key={ticker}
                className={`${isCryptoSymbol(ticker) ? 'bg-orange-100 text-orange-800 border-orange-400' : 'bg-yellow-200 text-yellow-900 border-yellow-400'} px-3 py-1 rounded-full border font-semibold text-sm flex items-center`}
                title={ticker}
              >
                {tickerLabel(ticker)}
                <button
                  type="button"
                  className="ml-2 text-yellow-900 hover:text-red-500 font-bold"
                  onClick={() => handleRemoveTicker(ticker)}
                  title="Remove ticker"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
        {/* Tags */}
        <div>
          <label className="block mb-1 font-semibold">Tags</label>
          <DropdownMultiSelect
            options={DEFAULT_TAGS}
            selected={tags}
            onSelect={handleAddTag}
            placeholder="Select Tag"
          />
          <div className="flex gap-2 flex-wrap mt-2">
            {tags.map(tag => (
              <span
                key={tag}
                className="bg-yellow-200 text-yellow-900 px-3 py-1 rounded-full border border-yellow-400 font-semibold text-sm flex items-center"
              >
                {tag}
                <button
                  type="button"
                  className="ml-2 text-yellow-900 hover:text-red-500 font-bold"
                  onClick={() => handleRemoveTag(tag)}
                  title="Remove tag"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
        {/* Image upload */}
        <div>
          <label className="block mb-1 font-semibold">Attach Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="px-3 py-2 rounded w-full"
          />
          {imageUrl && (
            <div className="mt-2">
              <img src={imageUrl} alt="Preview" className="max-h-40 rounded" />
            </div>
          )}
        </div>
        {/* Status */}
        {/* <div>
          <label className="block mb-1 font-semibold">Status</label>
          <select
            className="border px-3 py-2 rounded w-full"
            value={status}
            onChange={e => setStatus(e.target.value)}
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div> */}
        {error && <div className="text-red-500">{error}</div>}
        {/* Buttons */}
        <div className="flex gap-4 mt-6">
          <button
            type="submit"
            className="bg-yellow-400 text-gray-900 px-6 py-2 rounded font-bold"
            disabled={loading}
          >
            {loading ? (articleId ? 'Updating...' : 'Creating...') : (articleId ? 'Update' : 'Create')}
          </button>
          <button
            type="button"
            className="bg-gray-300 text-gray-800 px-6 py-2 rounded font-bold"
            onClick={() => navigate('/articles')}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bg-blue-400 text-white px-6 py-2 rounded font-bold"
            onClick={() => setShowPreview(true)}
          >
            Preview
          </button>
        </div>
      </form>
      <ArticlePreviewModal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        article={{
          title,
          content,
          tags,
          tickers,
          guruName: guruList.find((g: any) => g.id === guruId)?.name || '',
          portfolioName: portfolioOptions.find((p: any) => p.id === portfolioId)?.name || '',
          cover: imageUrl,
        }}
      />
    </div>
  );
};

export default CreateArticlePage;